import { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { SearchableSelect } from "@/components/ui/searchable-select";
import { getLegacyCustomerName, Task, TaskAttachment, LOAN_TYPE_LABELS } from "@/types";
import { toast } from "sonner";
import { useChannelPartners } from "@/hooks/useChannelPartners";
import { useBankers } from "@/hooks/useBankers";
import { useLeads } from "@/hooks/useLeads";
import { TEST_ADMIN_USER_ID } from "@/lib/constants";
import { useAuth } from "@/contexts/AuthContext";
import { format } from "date-fns";
import { X, Image, FileText, Share2, Users, CheckCircle2, RotateCcw, User, Camera, Upload, ChevronDown, Bell, Edit2, Phone, Download, ExternalLink, Loader2, AlertCircle, MessageCircle, Mic, Calendar, CreditCard, Building } from "lucide-react";
import { useMobileFormScroll } from "@/hooks/useFormScroll";
import { uploadTaskAttachment, deleteTaskAttachment } from "@/services/taskService";
import { useAppConfig } from "@/hooks/useAppConfig";
import { capturePhoto } from "@/services/cameraService";
import { ImageViewer } from "@/components/common/ImageViewer";
import { getAllActiveUsers } from "@/services/userService";
import { useModal } from "@/contexts/ModalContext";
import { useSpeechRecognition } from "@/hooks/useSpeechRecognition";

interface ViewEditTaskDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  task: Task;
  onSave: (task: Task) => void;
}

export function ViewEditTaskDialog({ open, onOpenChange, task, onSave }: ViewEditTaskDialogProps) {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { config } = useAppConfig();
  const { partners: channelPartners = [], loading: partnersLoading } = useChannelPartners();
  const { bankers, loading: bankersLoading, isFavorite: isBankerFavorite } = useBankers();
  const { leads, loading: leadsLoading } = useLeads();
  const [isEditing, setIsEditing] = useState(false);
  const [description, setDescription] = useState(task.description);
  const [linkType, setLinkType] = useState<'customer' | 'partner' | 'sm'>('customer');
  const [leadId, setLeadId] = useState<string>(task.leadId || '');
  const [partnerId, setPartnerId] = useState<string>(task.connectorId || '');
  const [smId, setSmId] = useState<string>(task.smContactId || '');
  const [dueDate, setDueDate] = useState(format(task.dueDate, "yyyy-MM-dd"));
  const [dueTime, setDueTime] = useState(format(task.dueDate, "HH:mm"));
  const [priority, setPriority] = useState<'low' | 'medium' | 'high'>(task.priority);
  const [attachments, setAttachments] = useState<TaskAttachment[]>(task.attachments || []);
  const [linkOpen, setLinkOpen] = useState(true);
  const [shareOpen, setShareOpen] = useState(false);
  const [sharedWith, setSharedWith] = useState<string[]>(task.sharedWith || []);
  const [shareWithAll, setShareWithAll] = useState(false);
  const [confirmCompleteOpen, setConfirmCompleteOpen] = useState(false);
  const [confirmUncompleteOpen, setConfirmUncompleteOpen] = useState(false);
  const [confirmDeleteFileOpen, setConfirmDeleteFileOpen] = useState(false);
  const [fileToDelete, setFileToDelete] = useState<TaskAttachment | null>(null);
  const [pendingFiles, setPendingFiles] = useState<File[]>([]);
  const [isSaving, setIsSaving] = useState(false);
  const [viewingImageIndex, setViewingImageIndex] = useState<number | null>(null);
  const [availableUsers, setAvailableUsers] = useState<any[]>([]);
  const formRef = useRef<HTMLDivElement>(null);
  useMobileFormScroll(formRef);
  const { isListening, toggleVoiceInput } = useSpeechRecognition({
    onTranscript: (text) => setDescription(prev => {
      const newText = prev ? `${prev} ${text}` : text;
      return newText.substring(0, 500);
    })
  });
  
  // Cleanup object URLs when component unmounts or files change
  useEffect(() => {
    return () => {
      // Revoke all object URLs to prevent memory leaks
      pendingFiles.forEach(file => {
        if (file.type.startsWith('image/')) {
          URL.revokeObjectURL(URL.createObjectURL(file));
        }
      });
    };
  }, [pendingFiles]);

  // Get the linked lead, partner, and banker
  const lead = leadId && leadId !== 'none' ? leads.find(l => l.id === leadId) : null;
  const partner = partnerId && partnerId !== 'none' ? channelPartners.find(c => c.id === partnerId) : null;
  const banker = smId && smId !== 'none' ? bankers.find(sm => sm.id === smId) : null;

  // Check if user has access to linked entities (they're already filtered by hooks)
  // If lead/partner/banker is null but ID exists, user doesn't have access
  const hasLeadAccess = !leadId || leadId === 'none' || !!lead;
  const hasPartnerAccess = !partnerId || partnerId === 'none' || !!partner;
  const hasBankerAccess = !smId || smId === 'none' || !!banker;

  // Call and WhatsApp handlers
  const handleCallClick = (e: React.MouseEvent, phone: string) => {
    e.stopPropagation();
    window.location.href = `tel:${phone}`;
  };
  
  const handleWhatsAppClick = (e: React.MouseEvent, phone: string) => {
    e.stopPropagation();
    window.open(`https://wa.me/91${phone.replace(/[^0-9]/g, '')}`, '_blank');
  };

  // Load users from Firebase
  useEffect(() => {
    const loadUsers = async () => {
      try {
        const usersData = await getAllActiveUsers();
        setAvailableUsers(usersData.filter(u => (u.userId || u.id) !== TEST_ADMIN_USER_ID));
      } catch (error) {
        console.error('Error loading users:', error);
      }
    };
    loadUsers();
  }, []);

  // Helper function to safely convert Firebase Timestamp to Date
  const toSafeDate = (dateValue: any): Date | null => {
    if (!dateValue) return null;
    
    // Already a Date object
    if (dateValue instanceof Date) {
      return isNaN(dateValue.getTime()) ? null : dateValue;
    }
    
    // Firestore Timestamp object
    if (dateValue.toDate && typeof dateValue.toDate === 'function') {
      try {
        const date = dateValue.toDate();
        return isNaN(date.getTime()) ? null : date;
      } catch {
        return null;
      }
    }
    
    // Try to parse as timestamp or date string
    try {
      const date = new Date(dateValue);
      return isNaN(date.getTime()) ? null : date;
    } catch {
      return null;
    }
  };

  // Helper function to resolve createdBy from user ID to name if needed
  const getCreatedByDisplay = (): string => {
    // Use denormalized name first (new tasks have this)
    if (task.createdByName) return task.createdByName;

    if (!task.createdBy) return 'Unknown';

    // If it looks like a user ID, try to resolve it from loaded users (legacy tasks)
    if (task.createdBy.includes('user_') || (task.createdBy.length > 20 && !task.createdBy.includes(' '))) {
      const foundUser = availableUsers.find(u => (u.userId || u.id) === task.createdBy);
      return foundUser?.name || task.createdBy;
    }

    // Very old legacy data — already stored as a name
    return task.createdBy;
  };

  // Reset to view mode when dialog opens
  useEffect(() => {
    if (open) {
      setIsEditing(false);
      setDescription(task.description);
      
      // Determine linkType based on what's linked
      if (task.connectorId) {
        setLinkType('partner');
      } else if (task.smContactId) {
        setLinkType('sm');
      } else {
        setLinkType('customer');
      }
      
      setLeadId(task.leadId || '');
      setPartnerId(task.connectorId || '');
      setSmId(task.smContactId || '');
      setDueDate(format(task.dueDate, "yyyy-MM-dd"));
      setDueTime(format(task.dueDate, "HH:mm"));
      setPriority(task.priority);
      setAttachments(task.attachments || []);
      setPendingFiles([]);
      setSharedWith(task.sharedWith || []);
      setShareWithAll(false);
      setLinkOpen(true);
      setShareOpen(false);
      setIsSaving(false);
      setConfirmDeleteFileOpen(false);
      setFileToDelete(null);
    }
  }, [open, task]);

  // Auto-populate sharedWith when linked lead changes and is shared
  // Only applies when editing (isEditing = true) to allow manual edits
  useEffect(() => {
    if (!isEditing) return;
    
    // Only auto-share for leads, not for channel partners or bankers
    if (partnerId || smId) {
      return;
    }

    if (!leadId || leadId === 'none') {
      return;
    }

    const currentLead = lead;
    if (currentLead?.sharedWith && currentLead.sharedWith.length > 0) {
      // Lead is shared - merge auto-shared users with manually selected ones
      // This preserves any manual selections while adding lead's sharedWith users
      setSharedWith(prev => {
        const merged = new Set(prev);
        currentLead.sharedWith.forEach(userId => merged.add(userId));
        return Array.from(merged);
      });
    } else if (currentLead && !currentLead.sharedWith) {
      // Lead exists but is not shared - keep current selection but don't auto-add
      // User could have manually shared it if they wanted to
      return;
    }
  }, [leadId, lead, isEditing, partnerId, smId]);

  const handleFileSelection = (files: FileList) => {
    const maxSizeMB = config.maxFileUploadSizeMB;
    const maxSizeBytes = maxSizeMB * 1024 * 1024;
    
    // Check total file count (existing + pending + new)
    const totalFiles = attachments.length + pendingFiles.length + files.length;
    if (totalFiles > config.maxFileUploads) {
      toast.error(`Maximum ${config.maxFileUploads} files allowed per reminder`);
      return;
    }
    
    const validFiles: File[] = [];
    
    for (const file of Array.from(files)) {
      // Check file size
      if (file.size > maxSizeBytes) {
        toast.error(`${file.name} exceeds maximum size of ${maxSizeMB}MB`);
        continue;
      }
      
      validFiles.push(file);
    }
    
    if (validFiles.length > 0) {
      setPendingFiles(prev => [...prev, ...validFiles]);
      toast.success(`Added ${validFiles.length} file${validFiles.length > 1 ? 's' : ''} for upload`);
    }
  };

  const handleRemoveAttachment = async (id: string) => {
    // Find the attachment to delete
    const attachment = attachments.find(a => a.id === id);
    if (attachment) {
      setFileToDelete(attachment);
      setConfirmDeleteFileOpen(true);
    }
  };
  
  const confirmDeleteFile = async () => {
    if (!fileToDelete) return;
    
    try {
      // Delete from Firebase Storage if it has a URL
      if (fileToDelete.url) {
        await deleteTaskAttachment(fileToDelete.url);
      }
      
      // Remove from attachments list
      setAttachments(prev => prev.filter(a => a.id !== fileToDelete.id));
      
      toast.success(`${fileToDelete.name} removed`);
    } catch (error) {
      console.error('Error deleting attachment:', error);
      toast.error(`Failed to delete ${fileToDelete.name}`);
    } finally {
      setConfirmDeleteFileOpen(false);
      setFileToDelete(null);
    }
  };
  
  const handleRemoveFile = (fileName: string) => {
    // Remove from pending files (not yet uploaded)
    setPendingFiles(prev => prev.filter(f => f.name !== fileName));
    toast.success(`Removed ${fileName}`);
  };

  const handleSave = async () => {
    // Validate description
    if (!description.trim()) {
      toast.error("Please enter what to remind");
      return;
    }

    // Validate description length (max 500 characters)
    if (description.length > 500) {
      toast.error("Description must be 500 characters or less");
      return;
    }

    // Validate date and time are provided
    if (!dueDate) {
      toast.error("Please select a due date");
      return;
    }

    if (!dueTime) {
      toast.error("Please select a due time");
      return;
    }

    // Validate due date format
    const dueDateTime = new Date(`${dueDate}T${dueTime}`);
    if (isNaN(dueDateTime.getTime())) {
      toast.error("Invalid date or time format");
      return;
    }

    // Allow past times for editing reminders (user may want to update overdue reminders)

    // Validate total attachment count (existing + new)
    const totalFiles = attachments.length + pendingFiles.length;
    if (totalFiles > config.maxFileUploads) {
      toast.error(`Maximum ${config.maxFileUploads} files allowed per reminder`);
      return;
    }
    
    setIsSaving(true);
    
    try {
      // Upload all pending files
      const uploadedAttachments: TaskAttachment[] = [...attachments];
      
      if (pendingFiles.length > 0) {
        toast.info(`Uploading ${pendingFiles.length} file${pendingFiles.length > 1 ? 's' : ''}...`);
        
        for (const file of pendingFiles) {
          try {
            const attachment = await uploadTaskAttachment(file, task.createdBy, task.id);
            uploadedAttachments.push(attachment);
          } catch (error) {
            console.error('Upload error:', error);
            toast.error(`Failed to upload ${file.name}`);
            setIsSaving(false);
            return;
          }
        }
      }

      const dueDateTime = new Date(`${dueDate}T${dueTime}`);
      
      onSave({
        ...task,
        title: description.split('\n')[0].substring(0, 50), // Use first line of description as title
        description,
        leadId: leadId && leadId !== 'none' ? leadId : undefined,
        connectorId: partnerId && partnerId !== 'none' ? partnerId : undefined,
        smContactId: smId && smId !== 'none' ? smId : undefined,
        dueDate: dueDateTime,
        priority,
        attachments: uploadedAttachments,
        sharedWith,
      });

      const shareMessage = sharedWith.length > 0 
        ? ` (shared with ${shareWithAll ? 'all' : sharedWith.length + ' colleague' + (sharedWith.length > 1 ? 's' : '')})`
        : '';
      toast.success(`Reminder updated${shareMessage}`);
      setPendingFiles([]);
      setIsSaving(false);
      setIsEditing(false); // Return to view mode after saving
    } catch (error) {
      console.error('Error saving task:', error);
      toast.error('Failed to update reminder');
      setIsSaving(false);
    }
  };

  const handleToggleShareMember = (memberId: string) => {
    setSharedWith(prev => 
      prev.includes(memberId) 
        ? prev.filter(id => id !== memberId)
        : [...prev, memberId]
    );
    setShareWithAll(false);
  };

  const handleShareWithAll = () => {
    if (shareWithAll) {
      setSharedWith([]);
      setShareWithAll(false);
    } else {
      setSharedWith(availableUsers.map(m => m.userId || m.id));
      setShareWithAll(true);
    }
  };

  const handleMarkComplete = () => {
    onSave({ ...task, completed: true });
    toast.success("Reminder marked as complete");
    setConfirmCompleteOpen(false);
    onOpenChange(false);
  };

  const handleUnmarkComplete = () => {
    onSave({ ...task, completed: false });
    toast.success("Reminder marked as pending");
    setConfirmUncompleteOpen(false);
    onOpenChange(false);
  };

  const handleShare = (members: string[]) => {
    setSharedWith(members);
    onSave({ ...task, sharedWith: members });
  };

  const handleFileUpload = () => {
    const input = document.createElement('input');
    input.type = 'file';
    input.multiple = true;
    input.accept = config.allowedFileTypes.join(',');
    input.onchange = (e) => {
      const target = e.target as HTMLInputElement;
      if (target.files && target.files.length > 0) {
        handleFileSelection(target.files);
      }
    };
    input.click();
  };

  const handleCameraCapture = async () => {
    try {
      const photoFile = await capturePhoto(90);
      
      if (photoFile) {
        // Check file size
        const maxSizeMB = config.maxFileUploadSizeMB;
        const maxSizeBytes = maxSizeMB * 1024 * 1024;
        
        if (photoFile.size > maxSizeBytes) {
          toast.error(`Photo exceeds maximum size of ${maxSizeMB}MB`);
          return;
        }
        
        setPendingFiles(prev => [...prev, photoFile]);
        toast.success("Photo captured successfully!");
      }
    } catch (error) {
      console.error("Error capturing photo:", error);
      toast.error("Failed to capture photo. Please check camera permissions.");
    }
  };

  // Helper function to view/open files
  const handleViewFile = (attachment: TaskAttachment) => {
    if (!attachment.url) {
      toast.error("File not available");
      return;
    }

    if (attachment.type.startsWith('image/')) {
      // Open image in new tab
      const newWindow = window.open();
      if (newWindow) {
        newWindow.document.write(`
          <html>
            <head>
              <title>${attachment.name}</title>
              <style>
                body { margin: 0; display: flex; justify-content: center; align-items: center; background: #000; }
                img { max-width: 100%; max-height: 100vh; object-fit: contain; }
              </style>
            </head>
            <body>
              <img src="${attachment.url}" alt="${attachment.name}" />
            </body>
          </html>
        `);
      }
      toast.success(`Opening ${attachment.name}`);
    } else if (attachment.type === 'application/pdf') {
      // Open PDF in new tab
      window.open(attachment.url, '_blank');
      toast.success(`Opening PDF: ${attachment.name}`);
    } else {
      // For other files, trigger download
      handleDownloadFile(attachment);
    }
  };

  // Helper function to download files
  const handleDownloadFile = (attachment: TaskAttachment) => {
    if (!attachment.url) {
      toast.error("File not available");
      return;
    }

    const link = document.createElement('a');
    link.href = attachment.url;
    link.download = attachment.name;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    toast.success(`Downloading ${attachment.name}`);
  };

  // Register/unregister with ModalContext for centralized back button handling
  const { registerModal, unregisterModal } = useModal();
  
  useEffect(() => {
    if (!open) return;

    // Determine priority based on what's currently open (higher priority for nested modals)
    let priority = 1;
    if (viewingImageIndex !== null) priority = 3;
    else if (confirmCompleteOpen || confirmUncompleteOpen || confirmDeleteFileOpen) priority = 2;
    
    const handleClose = () => {
      if (viewingImageIndex !== null) {
        setViewingImageIndex(null);
      } else if (confirmCompleteOpen) {
        setConfirmCompleteOpen(false);
      } else if (confirmUncompleteOpen) {
        setConfirmUncompleteOpen(false);
      } else if (confirmDeleteFileOpen) {
        setConfirmDeleteFileOpen(false);
      } else {
        onOpenChange(false);
      }
    };

    registerModal('view-edit-task-dialog', handleClose, priority);

    return () => {
      unregisterModal('view-edit-task-dialog');
    };
  }, [open, viewingImageIndex, confirmCompleteOpen, confirmUncompleteOpen, confirmDeleteFileOpen, onOpenChange, registerModal, unregisterModal]);

  // Prevent closing dialog when image viewer is open
  const handleOpenChange = (newOpen: boolean) => {
    if (!newOpen && viewingImageIndex !== null) {
      // If trying to close the dialog while viewing an image, just close the image viewer
      setViewingImageIndex(null);
      return;
    }
    onOpenChange(newOpen);
  };

  return (
    <>
    <Sheet open={open} onOpenChange={handleOpenChange}>
      <SheetContent side="bottom" className="h-[90vh] rounded-t-3xl flex flex-col">
        <SheetHeader className="pb-4 flex-shrink-0">
          <SheetTitle className="text-xl font-bold">
            {isEditing ? (
              <>
                Edit Reminder
                {(lead || partner || banker) && (
                  <span className="text-base font-normal text-muted-foreground block mt-1">
                    For: {lead ? getLegacyCustomerName(lead) : partner ? partner.name : banker ? banker.name : ''}
                  </span>
                )}
              </>
            ) : "Reminder Details"}
          </SheetTitle>
        </SheetHeader>
        
        <div className="flex-1 overflow-y-auto px-6">
        {isEditing ? (
          // EDIT MODE - Exact same form as AddTaskDialog, pre-filled with data
          <div className="space-y-5 pb-8" ref={formRef}>

              {/* SECTION: What to remind */}
              <div className="space-y-3">
                <div className="flex items-center gap-2 pb-1">
                  <Mic className="h-4 w-4 text-green-600" />
                  <h3 className="font-semibold text-green-800">What to remind?</h3>
                </div>
                <div className="space-y-2">
                  <div className="relative">
                    <Textarea
                      value={description}
                      onChange={(e) => setDescription(e.target.value)}
                      placeholder="Enter reminder details or tap mic to speak..."
                      rows={4}
                      maxLength={500}
                      className="h-auto min-h-[100px] rounded-xl resize-none placeholder:text-muted-foreground/50 pr-12"
                    />
                    <Button
                      type="button"
                      variant={isListening ? "default" : "ghost"}
                      size="icon"
                      className={`absolute right-2 top-2 h-8 w-8 rounded-full ${isListening ? 'bg-red-500 hover:bg-red-600 animate-pulse' : ''}`}
                      onClick={toggleVoiceInput}
                      title={isListening ? "Stop listening" : "Start voice input"}
                    >
                      <Mic className={`h-4 w-4 ${isListening ? 'text-white' : ''}`} />
                    </Button>
                  </div>
                  <div className="flex justify-between items-center text-xs">
                    <span className="text-muted-foreground">
                      {isListening ? '🔴 Listening... tap mic to stop' : 'Tap mic for voice input • Max 500 characters'}
                    </span>
                    <span className={description.length > 450 ? "text-warning font-medium" : "text-muted-foreground"}>
                      {description.length}/500
                    </span>
                  </div>
                </div>
              </div>

              {/* SECTION: Due Date & Time */}
              <div className="space-y-3">
                <div className="flex items-center gap-2 pb-1">
                  <Calendar className="h-4 w-4 text-green-600" />
                  <h3 className="font-semibold text-green-800">Due Date & Time</h3>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-2">
                    <Label className="text-sm font-medium">Date <span className="text-red-500">*</span></Label>
                    <Input
                      type="date"
                      value={dueDate}
                      onChange={(e) => setDueDate(e.target.value)}
                      className="h-12 placeholder:text-muted-foreground/50 [&::-webkit-calendar-picker-indicator]:ml-auto [&::-webkit-calendar-picker-indicator]:opacity-50 hover:[&::-webkit-calendar-picker-indicator]:opacity-100"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-sm font-medium">Time <span className="text-red-500">*</span></Label>
                    <Input
                      type="time"
                      value={dueTime}
                      onChange={(e) => setDueTime(e.target.value)}
                      className="h-12 placeholder:text-muted-foreground/50 [&::-webkit-calendar-picker-indicator]:ml-auto [&::-webkit-calendar-picker-indicator]:opacity-50 hover:[&::-webkit-calendar-picker-indicator]:opacity-100"
                    />
                  </div>
                </div>
              </div>

              {/* SECTION: Attachments */}
              <div className="space-y-3">
                <div className="flex items-center gap-2 pb-1">
                  <Image className="h-4 w-4 text-green-600" />
                  <h3 className="font-semibold text-green-800">Attachments</h3>
                </div>
                
                {/* Image Previews Grid - Uploaded Attachments */}
                {attachments.filter(att => att.type.startsWith('image/')).length > 0 && (
                  <div className="grid grid-cols-3 gap-2">
                    {attachments.filter(att => att.type.startsWith('image/')).map((att, index) => (
                      <div key={att.id} className="relative aspect-square rounded-lg overflow-hidden border-2 border-dashed border-border">
                        <img 
                          src={att.url} 
                          alt={att.name}
                          className="w-full h-full object-cover cursor-pointer hover:opacity-90 transition-opacity"
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            setViewingImageIndex(index);
                          }}
                        />
                        <button
                          onClick={() => handleRemoveAttachment(att.id)}
                          disabled={isSaving}
                          className="absolute top-1 right-1 bg-destructive/80 text-destructive-foreground rounded-full p-1.5 hover:bg-destructive transition-colors shadow-lg"
                          type="button"
                        >
                          <X className="h-3.5 w-3.5" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
                
                {/* Image Previews Grid - Pending Files */}
                {pendingFiles.filter(f => f.type.startsWith('image/')).length > 0 && (
                  <div className="grid grid-cols-3 gap-2">
                    {pendingFiles.filter(f => f.type.startsWith('image/')).map((file, index) => {
                      const imageUrl = URL.createObjectURL(file);
                      const uploadedImagesCount = attachments.filter(att => att.type.startsWith('image/')).length;
                      return (
                        <div key={`img-${file.name}-${index}`} className="relative aspect-square rounded-lg overflow-hidden border-2 border-dashed border-border">
                          <img 
                            src={imageUrl} 
                            alt={file.name}
                            className="w-full h-full object-cover cursor-pointer hover:opacity-90 transition-opacity"
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              setViewingImageIndex(uploadedImagesCount + index);
                            }}
                          />
                          <button
                            onClick={() => handleRemoveFile(file.name)}
                            disabled={isSaving}
                            className="absolute top-1 right-1 bg-destructive/80 text-destructive-foreground rounded-full p-1.5 hover:bg-destructive transition-colors shadow-lg"
                            type="button"
                          >
                            <X className="h-3.5 w-3.5" />
                          </button>
                          <div className="absolute bottom-0 left-0 right-0 bg-black/50 text-white text-xs p-1 text-center">
                            Not uploaded yet
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
                
                {/* Non-Image Files - Uploaded */}
                {attachments.filter(att => !att.type.startsWith('image/')).length > 0 && (
                  <div className="flex flex-wrap gap-2">
                    {attachments.filter(att => !att.type.startsWith('image/')).map(att => (
                      <Badge key={att.id} variant="outline" className="flex items-center gap-1.5 py-1.5 px-2.5 border-dashed">
                        <FileText className="h-3.5 w-3.5" />
                        <span className="max-w-[100px] truncate text-xs">{att.name}</span>
                        <button 
                          onClick={() => handleRemoveAttachment(att.id)} 
                          type="button"
                          className="ml-0.5"
                          disabled={isSaving}
                        >
                          <X className="h-3 w-3" />
                        </button>
                      </Badge>
                    ))}
                  </div>
                )}
                
                {/* Non-Image Files - Pending */}
                {pendingFiles.filter(f => !f.type.startsWith('image/')).length > 0 && (
                  <div className="flex flex-wrap gap-2">
                    {pendingFiles.filter(f => !f.type.startsWith('image/')).map((file, index) => (
                      <Badge key={`${file.name}-${index}`} variant="outline" className="flex items-center gap-1.5 py-1.5 px-2.5 border-dashed">
                        <FileText className="h-3.5 w-3.5" />
                        <span className="max-w-[100px] truncate text-xs">{file.name}</span>
                        <button 
                          onClick={() => handleRemoveFile(file.name)} 
                          type="button"
                          className="ml-0.5"
                          disabled={isSaving}
                        >
                          <X className="h-3 w-3" />
                        </button>
                      </Badge>
                    ))}
                  </div>
                )}

                {/* Upload Options */}
                <div className="border-2 border-dashed border-border rounded-xl p-4">
                  <div className="flex flex-col gap-2">
                    <Button 
                      variant="outline" 
                      size="sm" 
                      className="w-full justify-start rounded-xl"
                      onClick={handleCameraCapture}
                      type="button"
                      disabled={isSaving}
                    >
                      <Camera className="h-4 w-4 mr-2" />
                      Take Photo
                    </Button>
                    <Button 
                      variant="outline" 
                      size="sm" 
                      className="w-full justify-start rounded-xl"
                      onClick={handleFileUpload}
                      type="button"
                      disabled={isSaving}
                    >
                      <Upload className="h-4 w-4 mr-2" />
                      Upload File
                    </Button>
                  </div>
                  <p className="text-xs text-muted-foreground mt-3 text-center">
                    {(attachments.length + pendingFiles.length) > 0 
                      ? `${attachments.length + pendingFiles.length} file${(attachments.length + pendingFiles.length) > 1 ? 's' : ''} attached`
                      : `Photos, documents, PDFs • Max ${config.maxFileUploads} files, ${config.maxFileUploadSizeMB}MB each`
                    }
                  </p>
                </div>
              </div>

              {/* SECTION: Customer (Optional) */}
              <div className="space-y-3">
                <div className="flex items-center gap-2 pb-1">
                  <User className="h-4 w-4 text-green-600" />
                  <h3 className="font-semibold text-green-800">Customer</h3>
                  <span className="text-xs text-muted-foreground">(Optional)</span>
                </div>
                <div className="space-y-2">
                  <Select value={leadId} onValueChange={setLeadId} disabled={leadsLoading}>
                    <SelectTrigger className="h-12">
                      <SelectValue placeholder={leadsLoading ? "Loading customers..." : "Select customer"} />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">No customer</SelectItem>
                      {leads.map(lead => (
                        <SelectItem key={lead.id} value={lead.id}>
                          {getLegacyCustomerName(lead)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* SECTION: Banker Contact (Optional) */}
              <div className="space-y-3">
                <div className="flex items-center gap-2 pb-1">
                  <CreditCard className="h-4 w-4 text-green-600" />
                  <h3 className="font-semibold text-green-800">Banker Contact</h3>
                  <span className="text-xs text-muted-foreground">(Optional)</span>
                </div>
                <div className="space-y-2">
                  <SearchableSelect
                    options={[
                      { value: "none", label: "No banker" },
                      ...[...bankers]
                        .sort((a, b) => {
                          const aFav = isBankerFavorite(a.id);
                          const bFav = isBankerFavorite(b.id);
                          if (aFav && !bFav) return -1;
                          if (!aFav && bFav) return 1;
                          return a.name.localeCompare(b.name);
                        })
                        .map(sm => ({
                          value: sm.id,
                          label: `${isBankerFavorite(sm.id) ? '★ ' : ''}${sm.name} - ${sm.bank}`,
                          displayLabel: sm.name
                        }))
                    ]}
                    value={smId}
                    onValueChange={setSmId}
                    placeholder={bankersLoading ? "Loading bankers..." : "Select banker"}
                    searchPlaceholder="Search bankers..."
                    emptyText="No banker found."
                  />
                </div>
              </div>

              {/* SECTION: Partner (Optional) */}
              <div className="space-y-3">
                <div className="flex items-center gap-2 pb-1">
                  <Building className="h-4 w-4 text-green-600" />
                  <h3 className="font-semibold text-green-800">Partner</h3>
                  <span className="text-xs text-muted-foreground">(Optional)</span>
                </div>
                <div className="space-y-2">
                  <SearchableSelect
                    options={[
                      { value: "none", label: "No partner" },
                      ...[...channelPartners]
                        .sort((a, b) => {
                          if (a.isFavorite && !b.isFavorite) return -1;
                          if (!a.isFavorite && b.isFavorite) return 1;
                          return a.name.localeCompare(b.name);
                        })
                        .map(c => ({
                          value: c.id,
                          label: `${c.isFavorite ? '★ ' : ''}${c.name} (${c.type})`
                        }))
                    ]}
                    value={partnerId}
                    onValueChange={setPartnerId}
                    placeholder={partnersLoading ? "Loading partners..." : "Select partner"}
                    searchPlaceholder="Search partners..."
                    emptyText="No partner found."
                  />
                </div>
              </div>

              {/* SECTION: Share with Colleagues */}
              <div className="space-y-3">
                <div className="flex items-center gap-2 pb-1">
                  <Share2 className="h-4 w-4 text-green-600" />
                  <h3 className="font-semibold text-green-800">Share with Colleagues</h3>
                </div>
                <Collapsible open={shareOpen} onOpenChange={setShareOpen}>
                  <CollapsibleTrigger asChild>
                    <Button variant="outline" className="w-full justify-between h-12" type="button">
                      <span className="flex items-center gap-2">
                        <span>
                          {sharedWith.length > 0 
                            ? `Sharing with ${shareWithAll ? 'all' : sharedWith.length} colleague${sharedWith.length > 1 ? 's' : ''}`
                            : 'Tap to share'
                          }
                        </span>
                      </span>
                      <ChevronDown className={`h-4 w-4 transition-transform duration-200 ${shareOpen ? 'rotate-180' : ''}`} />
                    </Button>
                  </CollapsibleTrigger>
                  <CollapsibleContent className="pt-3 space-y-2">
                    <div 
                      className="flex items-center gap-3 p-3 rounded-xl border cursor-pointer hover:bg-secondary/50 transition-colors"
                      onClick={handleShareWithAll}
                    >
                      <Checkbox checked={shareWithAll} onCheckedChange={handleShareWithAll} />
                      <div className="flex-1">
                        <span className="text-sm font-medium">Share with all</span>
                      </div>
                      <Users className="h-4 w-4 text-muted-foreground" />
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      {availableUsers.map((member) => {
                        const memberId = member.userId || member.id;
                        return (
                          <div 
                            key={memberId}
                            className="flex items-center gap-2 p-2.5 rounded-xl border cursor-pointer hover:bg-secondary/50 transition-colors"
                            onClick={() => handleToggleShareMember(memberId)}
                          >
                            <Checkbox 
                              checked={sharedWith.includes(memberId)} 
                              onCheckedChange={() => handleToggleShareMember(memberId)}
                            />
                            <span className="text-sm truncate">{member.name.split(' ')[0]}</span>
                          </div>
                        );
                      })}
                    </div>
                  </CollapsibleContent>
                </Collapsible>
              </div>
        </div>
        ) : (
          // VIEW MODE
          <div className="space-y-5 pb-8">
            {/* Action Buttons at top */}
            <div className="grid grid-cols-2 gap-3">
              <Button 
                variant="secondary" 
                className="h-11 rounded-xl font-semibold shadow-sm" 
                onClick={() => setIsEditing(true)}
                type="button"
              >
                <Edit2 className="h-4 w-4 mr-2" />
                Edit
              </Button>
              {task.completed ? (
                <Button 
                  className="h-11 rounded-xl font-semibold bg-gradient-to-r from-warning to-warning/90 text-warning-foreground shadow-md hover:shadow-lg transition-shadow" 
                  onClick={() => setConfirmUncompleteOpen(true)}
                  type="button"
                >
                  <RotateCcw className="h-4 w-4 mr-2" />
                  Mark Pending
                </Button>
              ) : (
                <Button 
                  className="h-11 rounded-xl font-semibold bg-gradient-to-r from-success to-success/90 text-success-foreground shadow-md hover:shadow-lg transition-shadow" 
                  onClick={() => setConfirmCompleteOpen(true)}
                  type="button"
                >
                  <CheckCircle2 className="h-4 w-4 mr-2" />
                  Complete
                </Button>
              )}
            </div>

            {/* Description */}
            {task.description && (
              <div className="p-4 bg-gradient-to-br from-muted/50 to-muted/30 rounded-xl border border-border/50">
                <p className="text-sm leading-relaxed whitespace-pre-wrap break-words">{task.description}</p>
              </div>
            )}

            {/* Due Date/Time */}
            <div className="flex items-center gap-3 p-4 bg-gradient-to-br from-warning/5 to-warning/10 rounded-xl border border-warning/20">
              <div className="h-10 w-10 rounded-full bg-warning/20 flex items-center justify-center flex-shrink-0">
                <Bell className="h-5 w-5 text-warning" />
              </div>
              <div className="flex-1">
                <Label className="text-xs text-muted-foreground">Due Date & Time</Label>
                <p className="text-sm font-semibold mt-0.5">
                  {format(task.dueDate, "MMMM d, yyyy")} at {format(task.dueDate, "h:mm a")}
                </p>
                {task.snoozeCount && task.snoozeCount > 0 && ((() => {
                  const originalDate = toSafeDate(task.originalDueDate);
                  return (
                    <p className="text-xs text-muted-foreground mt-1">
                      Snoozed {task.snoozeCount} time{task.snoozeCount > 1 ? 's' : ''}
                      {originalDate && (
                        <span className="ml-1">
                          • Originally: {format(originalDate, "MMM d, h:mm a")}
                        </span>
                      )}
                    </p>
                  );
                })())}
              </div>
            </div>

            {/* Attachments with Preview & Download */}
            {task.attachments && task.attachments.length > 0 && (
              <div className="space-y-3">
                <Label className="text-xs text-muted-foreground flex items-center gap-2">
                  <FileText className="h-3.5 w-3.5" />
                  Attachments ({task.attachments.length})
                </Label>
                
                {/* Image Previews */}
                {task.attachments.filter(att => att.type.startsWith('image/')).length > 0 && (
                  <div className="space-y-2">
                    <p className="text-xs font-medium text-muted-foreground">Images</p>
                    <div className="grid grid-cols-2 gap-2">
                      {task.attachments.filter(att => att.type.startsWith('image/')).map((att, index) => (
                        <div 
                          key={att.id} 
                          className="relative group rounded-xl overflow-hidden border border-border/50 bg-muted/30 aspect-video cursor-pointer hover:border-primary/50 transition-colors"
                        >
                          {att.url ? (
                            <img 
                              src={att.url} 
                              alt={att.name}
                              className="absolute inset-0 w-full h-full object-cover"
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                setViewingImageIndex(index);
                              }}
                            />
                          ) : (
                            <div className="absolute inset-0 bg-gradient-to-br from-primary/5 to-primary/10 flex items-center justify-center">
                              <Image className="h-12 w-12 text-primary/40" />
                            </div>
                          )}
                          <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/60 to-transparent p-2">
                            <p className="text-[10px] text-white truncate">{att.name}</p>
                          </div>
                          {/* Download button overlay */}
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDownloadFile(att);
                            }}
                            className="absolute top-2 right-2 h-7 w-7 rounded-full bg-black/50 backdrop-blur-sm flex items-center justify-center transition-opacity"
                          >
                            <Download className="h-3.5 w-3.5 text-white" />
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* PDF Files */}
                {task.attachments.filter(att => att.type === 'application/pdf').length > 0 && (
                  <div className="space-y-2">
                    <p className="text-xs font-medium text-muted-foreground">PDF Documents</p>
                    <div className="space-y-2">
                      {task.attachments.filter(att => att.type === 'application/pdf').map(att => (
                        <div 
                          key={att.id}
                          className="flex items-center gap-3 p-3 rounded-xl border border-border/50 bg-gradient-to-br from-red-500/5 to-red-500/10 hover:border-red-500/30 transition-colors group"
                        >
                          <div className="h-10 w-10 rounded-lg bg-red-500/20 flex items-center justify-center flex-shrink-0">
                            <FileText className="h-5 w-5 text-red-600" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium truncate">{att.name}</p>
                            <p className="text-xs text-muted-foreground">
                              PDF Document {att.size ? `• ${(att.size / 1024).toFixed(0)} KB` : ''}
                            </p>
                          </div>
                          <div className="flex gap-1">
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8"
                              onClick={() => handleViewFile(att)}
                            >
                              <ExternalLink className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8"
                              onClick={() => handleDownloadFile(att)}
                            >
                              <Download className="h-4 w-4" />
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Other Files */}
                {task.attachments.filter(att => !att.type.startsWith('image/') && att.type !== 'application/pdf').length > 0 && (
                  <div className="space-y-2">
                    <p className="text-xs font-medium text-muted-foreground">Other Files</p>
                    <div className="space-y-2">
                      {task.attachments.filter(att => !att.type.startsWith('image/') && att.type !== 'application/pdf').map(att => (
                        <div 
                          key={att.id}
                          className="flex items-center gap-3 p-3 rounded-xl border border-border/50 bg-muted/30 hover:border-primary/30 transition-colors group"
                        >
                          <div className="h-10 w-10 rounded-lg bg-muted flex items-center justify-center flex-shrink-0">
                            <FileText className="h-5 w-5 text-muted-foreground" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium truncate">{att.name}</p>
                            <p className="text-xs text-muted-foreground">
                              {att.type.split('/')[1]?.toUpperCase() || 'File'} 
                              {att.size ? ` • ${(att.size / 1024).toFixed(0)} KB` : ''}
                            </p>
                          </div>
                          <div className="flex gap-1">
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8"
                              onClick={() => handleViewFile(att)}
                            >
                              <ExternalLink className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8"
                              onClick={() => handleDownloadFile(att)}
                            >
                              <Download className="h-4 w-4" />
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Linked Customer */}
            {leadId && leadId !== 'none' && (
              <div className="p-3 bg-gradient-to-br from-primary/5 to-primary/10 rounded-lg border border-primary/20">
                {hasLeadAccess && lead ? (
                  <div className="flex items-start gap-2.5">
                    <div className="h-8 w-8 rounded-full bg-primary/20 flex items-center justify-center flex-shrink-0">
                      <User className="h-4 w-4 text-primary" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <Label className="text-[10px] text-muted-foreground">Linked Customer</Label>
                      <p className="font-semibold text-sm mt-0.5 leading-tight">{getLegacyCustomerName(lead)}</p>
                      <p className="text-xs text-muted-foreground mt-0.5 leading-tight">
                        ₹{(lead.loanAmount || 0) / 100000}L
                        {lead.lender && ` • ${lead.lender}`}
                        {lead.loanType && ` • ${LOAN_TYPE_LABELS[lead.loanType]}`}
                      </p>
                      {lead.mobile && (
                        <div className="flex items-center gap-2 mt-2">
                          <Button
                            variant="secondary"
                            size="sm"
                            className="h-7 px-2"
                            onClick={(e) => handleCallClick(e, lead.mobile)}
                          >
                            <Phone className="h-3.5 w-3.5 mr-1" />
                            <span className="text-xs">Call</span>
                          </Button>
                          <Button
                            variant="success"
                            size="sm"
                            className="h-7 px-2"
                            onClick={(e) => handleWhatsAppClick(e, lead.mobile)}
                          >
                            <MessageCircle className="h-3.5 w-3.5 mr-1" />
                            <span className="text-xs">WhatsApp</span>
                          </Button>
                        </div>
                      )}
                    </div>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="flex-shrink-0 h-7 w-7"
                      onClick={() => {
                        navigate(`/leads/${lead.id}`);
                        onOpenChange(false);
                      }}
                    >
                      <ExternalLink className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                ) : (
                  <div className="flex items-start gap-2.5">
                    <div className="h-8 w-8 rounded-full bg-muted flex items-center justify-center flex-shrink-0">
                      <AlertCircle className="h-4 w-4 text-muted-foreground" />
                    </div>
                    <div className="flex-1">
                      <Label className="text-[10px] text-muted-foreground">Linked Customer</Label>
                      <p className="text-sm text-muted-foreground mt-0.5">No access to view details</p>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Linked Partner */}
            {partnerId && partnerId !== 'none' && (
              <div className="p-3 bg-gradient-to-br from-blue-500/5 to-blue-500/10 rounded-lg border border-blue-500/20">
                {hasPartnerAccess && partner ? (
                  <div className="flex items-start gap-2.5">
                    <div className="h-8 w-8 rounded-full bg-blue-500/20 flex items-center justify-center flex-shrink-0">
                      <Users className="h-4 w-4 text-blue-600" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <Label className="text-[10px] text-muted-foreground">Linked Channel Partner</Label>
                      <p className="font-semibold text-sm mt-0.5 leading-tight">{partner.name}</p>
                      <p className="text-xs text-muted-foreground mt-0.5 leading-tight">
                        {partner.whatsappNumber && `${partner.whatsappNumber}`}
                        {partner.city && ` • ${partner.city}`}
                      </p>
                      {partner.whatsappNumber && (
                        <div className="flex items-center gap-2 mt-2">
                          <Button
                            variant="secondary"
                            size="sm"
                          className="h-7 px-2"
                          onClick={(e) => handleCallClick(e, partner.whatsappNumber)}
                        >
                          <Phone className="h-3.5 w-3.5 mr-1" />
                          <span className="text-xs">Call</span>
                        </Button>
                        <Button
                          variant="success"
                          size="sm"
                          className="h-7 px-2"
                          onClick={(e) => handleWhatsAppClick(e, partner.whatsappNumber)}
                        >
                          <MessageCircle className="h-3.5 w-3.5 mr-1" />
                          <span className="text-xs">WhatsApp</span>
                        </Button>
                      </div>
                    )}
                  </div>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="flex-shrink-0 h-7 w-7"
                    onClick={() => {
                      navigate(`/tools/channel-partners/${partner.id}`);
                      onOpenChange(false);
                    }}
                  >
                    <ExternalLink className="h-3.5 w-3.5" />
                  </Button>
                </div>
                ) : (
                  <div className="flex items-start gap-2.5">
                    <div className="h-8 w-8 rounded-full bg-muted flex items-center justify-center flex-shrink-0">
                      <AlertCircle className="h-4 w-4 text-muted-foreground" />
                    </div>
                    <div className="flex-1">
                      <Label className="text-[10px] text-muted-foreground">Linked Channel Partner</Label>
                      <p className="text-sm text-muted-foreground mt-0.5">No access to view details</p>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Linked Banker */}
            {smId && smId !== 'none' && (
              <div className="p-3 bg-gradient-to-br from-green-500/5 to-green-500/10 rounded-lg border border-green-500/20">
                {hasBankerAccess && banker ? (
                  <div className="flex items-start gap-2.5">
                    <div className="h-8 w-8 rounded-full bg-green-500/20 flex items-center justify-center flex-shrink-0">
                      <Phone className="h-4 w-4 text-green-600" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <Label className="text-[10px] text-muted-foreground">Linked Banker Contact</Label>
                      <p className="font-semibold text-sm mt-0.5 leading-tight">{banker.name}</p>
                      <p className="text-xs text-muted-foreground mt-0.5 leading-tight">
                        {banker.bank && `${banker.bank}`}
                        {banker.phone && ` • ${banker.phone}`}
                      </p>
                      {banker.phone && (
                        <div className="flex items-center gap-2 mt-2">
                          <Button
                            variant="secondary"
                            size="sm"
                          className="h-7 px-2"
                          onClick={(e) => handleCallClick(e, banker.phone)}
                        >
                          <Phone className="h-3.5 w-3.5 mr-1" />
                          <span className="text-xs">Call</span>
                        </Button>
                        <Button
                          variant="success"
                          size="sm"
                          className="h-7 px-2"
                          onClick={(e) => handleWhatsAppClick(e, banker.phone)}
                        >
                          <MessageCircle className="h-3.5 w-3.5 mr-1" />
                          <span className="text-xs">WhatsApp</span>
                        </Button>
                      </div>
                    )}
                  </div>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="flex-shrink-0 h-7 w-7"
                    onClick={() => {
                      navigate(`/tools/sm-contact/${banker.id}`);
                      onOpenChange(false);
                    }}
                  >
                    <ExternalLink className="h-3.5 w-3.5" />
                  </Button>
                </div>
                ) : (
                  <div className="flex items-start gap-2.5">
                    <div className="h-8 w-8 rounded-full bg-muted flex items-center justify-center flex-shrink-0">
                      <AlertCircle className="h-4 w-4 text-muted-foreground" />
                    </div>
                    <div className="flex-1">
                      <Label className="text-[10px] text-muted-foreground">Linked Banker Contact</Label>
                      <p className="text-sm text-muted-foreground mt-0.5">No access to view details</p>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Shared With */}
            {task.sharedWith && task.sharedWith.length > 0 && (
              <div className="p-3 bg-info/5 rounded-xl border border-info/20">
                <Label className="text-xs text-muted-foreground flex items-center gap-2 mb-2">
                  <Users className="h-3.5 w-3.5" />
                  Shared with {task.sharedWith.length} colleague{task.sharedWith.length > 1 ? 's' : ''}
                </Label>
                <div className="flex flex-wrap gap-2">
                  {task.sharedWith.map(memberId => {
                    const member = availableUsers.find(m => (m.userId || m.id) === memberId);
                    return member ? (
                      <Badge key={memberId} variant="outline" className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-info/10">
                        <User className="h-3 w-3" />
                        <span className="text-xs">{member.name}</span>
                      </Badge>
                    ) : null;
                  })}
                </div>
              </div>
            )}

            {/* Metadata Footer */}
            <div className="pt-4 mt-4 border-t border-border/50 text-xs text-muted-foreground space-y-1">
              <p>Created by {getCreatedByDisplay()}</p>
              {(() => {
                const createdDate = toSafeDate(task.createdAt);
                const updatedDate = toSafeDate(task.updatedAt);
                
                return (
                  <>
                    {createdDate && (
                      <p>Created on {format(createdDate, "MMM d, yyyy 'at' h:mm a")}</p>
                    )}
                    {updatedDate && (!createdDate || updatedDate.getTime() !== createdDate.getTime()) && (
                      <p>Last updated {format(updatedDate, "MMM d, yyyy 'at' h:mm a")}</p>
                    )}
                  </>
                );
              })()}
            </div>
          </div>
        )}
        </div>

        {/* Fixed Bottom Action Bar - Edit Mode */}
        {isEditing && (
          <div className="flex-shrink-0 bg-background pt-4 pb-2 border-t">
            <Button 
              className="w-full h-14 text-base font-semibold rounded-xl bg-gradient-to-r from-[#2d5a6b] via-[#3a7680] to-[#4ea39f] hover:opacity-90 transition-all mt-6 disabled:opacity-50 disabled:cursor-not-allowed" 
              onClick={handleSave}
              type="button"
              disabled={isSaving}
            >
              {isSaving ? (
                <>
                  <Loader2 className="h-5 w-5 mr-2 animate-spin" />
                  {pendingFiles.length > 0 ? 'Uploading & Saving...' : 'Saving...'}
                </>
              ) : (
                'Save Changes'
              )}
            </Button>
          </div>
        )}
      </SheetContent>
    </Sheet>

      {/* Confirm Complete Dialog */}
      <AlertDialog open={confirmCompleteOpen} onOpenChange={setConfirmCompleteOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Mark as Completed?</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to mark this reminder as completed? It will be moved to completed items.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleMarkComplete}>
              Mark Completed
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Confirm Uncomplete Dialog */}
      <AlertDialog open={confirmUncompleteOpen} onOpenChange={setConfirmUncompleteOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Mark as Pending?</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to move this reminder back to pending?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleUnmarkComplete}>
              Mark as Pending
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Confirm Delete File Dialog */}
      <AlertDialog open={confirmDeleteFileOpen} onOpenChange={setConfirmDeleteFileOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete File?</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete "{fileToDelete?.name}"? This will permanently remove the file from storage and cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setFileToDelete(null)}>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDeleteFile} className="bg-destructive hover:bg-destructive/90">
              Delete File
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      
      {/* Image Viewer Modal */}
      {viewingImageIndex !== null && (() => {
        const uploadedImages = attachments.filter(att => att.type.startsWith('image/')).map(att => att.url || '');
        const pendingImages = pendingFiles.filter(f => f.type.startsWith('image/')).map(f => URL.createObjectURL(f));
        const allImages = [...uploadedImages, ...pendingImages];
        return (
          <ImageViewer
            isOpen={viewingImageIndex !== null}
            images={allImages}
            currentIndex={viewingImageIndex}
            onClose={() => setViewingImageIndex(null)}
            onIndexChange={setViewingImageIndex}
          />
        );
      })()}
    </>
  );
}
